import Node from 'nanogl-node'

import GameEvents from '@/webgl/runner/core/GameEvents'
import GameTime from '@/webgl/runner/core/GameTime'
import SystemRegistry from '@/webgl/runner/core/SystemRegistry'
import BackgroundEntity from '@/webgl/runner/entities/BackgroundEntity'
import { BonusEntity, BonusType } from '@/webgl/runner/entities/BonusEntity'
import { CollectibleEntity } from '@/webgl/runner/entities/CollectibleEntity'
import GameStateEntity from '@/webgl/runner/entities/GameStateEntity'
import { PlayerEntity } from '@/webgl/runner/entities/PlayerEntity'
import { SettingsEntity } from '@/webgl/runner/entities/SettingsEntity'
import ShoppingEntity from '@/webgl/runner/entities/ShoppingEntity'
import { TimeEntity } from '@/webgl/runner/entities/Time'
import GameRegistry from '@/webgl/runner/GameAssets'
import BackgroundSystem from '@/webgl/runner/systems/BackgroundSystem'
import BonusSystem from '@/webgl/runner/systems/BonusSystem'
import CollectibleSystem from '@/webgl/runner/systems/CollectibleSystem'
import InputSystem from '@/webgl/runner/systems/InputSystem'
import MusicSystem from '@/webgl/runner/systems/MusicSystem'
import PlayerSystem from '@/webgl/runner/systems/PlayerSystem'
import ScrollSystem from '@/webgl/runner/systems/ScrollSystem'
import TrackingSystem from '@/webgl/runner/systems/TrackingSystem'

export default class GameWorld {
  readonly systems: SystemRegistry = new SystemRegistry(this)
  readonly events: GameEvents = new GameEvents()
  readonly gameTime: GameTime = new GameTime(this)

  readonly registry = new GameRegistry()
  readonly time = new TimeEntity()
  readonly player = new PlayerEntity()
  readonly background = new BackgroundEntity()
  readonly settings = new SettingsEntity()
  readonly collectibles: CollectibleEntity[] = []
  readonly bonuses: BonusEntity[] = []
  readonly state

  public shopping: ShoppingEntity = null

  constructor(public readonly root: Node) {
    this.state = new GameStateEntity(this.events)

    this.systems
      .add(new TrackingSystem())
      .add(new ScrollSystem(this))
      .add(new BackgroundSystem(this))
      .add(new CollectibleSystem(this))
      .add(new InputSystem(this))
      .add(new PlayerSystem(this))
      .add(new BonusSystem(this))
      .add(new MusicSystem(this))
  }

  getTrackingSystem(): TrackingSystem {
    return this.systems.get(TrackingSystem)
  }

  start() {
    this.state.notify()
    this.systems.start()
  }

  dispose() {
    this.state.dispose()
    this.systems.dispose()
    this.events.dispose()
  }

  update(dt: number) {
    this.gameTime.update(dt)
    this.systems.forEach((s) => s.update(this.time))
  }

  destroyCollectible(entity: CollectibleEntity) {
    const parent = entity.node._parent
    if (parent !== null) parent.remove(entity.node)
    const idx = this.collectibles.indexOf(entity)
    this.collectibles.splice(idx, 1)
  }

  destroyAllCollectibles() {
    const collectibles = this.collectibles
    for (let i = collectibles.length - 1; i >= 0; i--) {
      if (collectibles[i].collected) continue
      this.destroyCollectible(collectibles[i])
    }
  }

  isBonusActive(type: BonusType): boolean {
    return this.bonuses.find((b) => b.type == type) != null
  }

  isBonusActiveAny(types: BonusType[]): boolean {
    return this.bonuses.find((b) => types.indexOf(b.type) != -1) != null
  }

  getActiveBonuses(filter: BonusType[]) {
    return this.bonuses.filter((b) => filter.indexOf(b.type) != -1)
  }

  addBonus(entity: BonusEntity) {
    if (this.isBonusActive(entity.type)) return
    this.bonuses.push(entity)
    this.state.addActiveBonus(entity.type)
  }

  destroyBonus(entity: BonusEntity) {
    const bonuses = this.bonuses
    const idx = bonuses.indexOf(entity)
    bonuses.splice(idx, 1)
    this.state.removeActiveBonus(entity.type)
  }

  destroyAllBonuses() {
    const bonuses = this.bonuses
    for (let i = bonuses.length - 1; i >= 0; i--) {
      this.destroyBonus(bonuses[i])
    }
  }
}
